// src/components/pages/PicksHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import Ball, { BallsRow } from "../ui/Ball";
import Icon from "../ui/Icon";
import Spinner from "../ui/Spinner";

import { LOTTERY, lotteryName } from "../../utils/lottery";

import {
  getAllGeneratedNumbers,
  formatUsd,
  formatDrawDate,
  getDrawStatus,
} from "../../services/resultsService";

/**
 * Picks history page.
 *
 * Same data flow as Dashboard. Each pick from
 * /site/lotto-analyzer/list-numbers-generated already carries its own
 * prizeValueUsd / hits / winner / extraMatched (overlaid server-side
 * by LottoAnalyzerService) plus its buyDraw → draw (with the official
 * numbers), so grouping, summaries and filters are all client-side.
 *
 * Picks are grouped by drawing (and by order inside a drawing); the
 * official result lives once in the group header and each pick is a
 * compact row: 5 numbers + special ball + one status badge.
 */
const PERIOD = {
  ALL:    "all",
  WEEK:   "7d",
  MONTH:  "30d",
  M_TD:   "month",
  YEAR:   "year",
  CUSTOM: "custom",
};

const STATUS = {
  ALL:     "all",
  PENDING: "pending",
  DRAWN:   "drawn",
};

const RESULT = {
  ALL:     "all",
  HITS:    "hits",
  WINNERS: "winners",
};

// Game rows per page. A drawing split across pages repeats its header
// (official result + summary) at the top of the next page.
const ROWS_PER_PAGE = 25;

export default function PicksHistory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const lotteryFilter = (searchParams.get("lottery") || "ALL").toUpperCase();
  const period        = (searchParams.get("period")  || PERIOD.ALL).toLowerCase();
  const statusFilter  = (searchParams.get("status")  || STATUS.ALL).toLowerCase();
  const resultFilter  = (searchParams.get("result")  || RESULT.ALL).toLowerCase();
  const fromParam     = searchParams.get("from") || "";
  const toParam       = searchParams.get("to")   || "";
  const pageParam     = parseInt(searchParams.get("page") || "1", 10);

  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [games, setGames]     = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrored(false);

    (async () => {
      try {
        const lotteries =
          lotteryFilter === LOTTERY.MEGA_MILLIONS ? [LOTTERY.MEGA_MILLIONS] :
          lotteryFilter === LOTTERY.POWER_BALL    ? [LOTTERY.POWER_BALL]    :
                                                    [LOTTERY.MEGA_MILLIONS, LOTTERY.POWER_BALL];

        const lotteryGames = await Promise.all(
          lotteries.map((t) => getAllGeneratedNumbers(t).catch(() => []))
        );
        if (cancelled) return;

        const flat = [];
        lotteries.forEach((type, i) => {
          (lotteryGames[i] || []).forEach((g) => flat.push(analyzeGame({ ...g, _type: type })));
        });
        setGames(flat);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [lotteryFilter]);

  const { fromTs, toTs, periodLabel } = useMemo(
    () => resolvePeriod(period, fromParam, toParam),
    [period, fromParam, toParam]
  );

  // Games inside the lottery / date / status scope. The summary and the
  // group headers are computed from this set; the result filter only
  // hides rows.
  const scoped = useMemo(() => {
    let out = games;

    if (fromTs !== null || toTs !== null) {
      out = out.filter((g) => {
        const ts = drawTs(g);
        if (!Number.isFinite(ts) || ts === 0) return false;
        if (fromTs !== null && ts < fromTs) return false;
        if (toTs   !== null && ts > toTs)   return false;
        return true;
      });
    }

    if (statusFilter !== STATUS.ALL) {
      out = out.filter((g) => {
        const { pending } = getDrawStatus(g?.buyDraw?.draw);
        return statusFilter === STATUS.PENDING ? pending : !pending;
      });
    }

    return out;
  }, [games, fromTs, toTs, statusFilter]);

  const summary = useMemo(() => summarize(scoped), [scoped]);

  const groups = useMemo(() => {
    const keep =
      resultFilter === RESULT.HITS    ? (g) => g._a.hasHit :
      resultFilter === RESULT.WINNERS ? (g) => g._a.prize > 0 :
                                        () => true;
    return groupByDrawing(scoped)
      .map((grp) => ({ ...grp, rows: grp.games.filter(keep) }))
      .filter((grp) => grp.rows.length > 0);
  }, [scoped, resultFilter]);

  const visibleRowCount = useMemo(
    () => groups.reduce((n, grp) => n + grp.rows.length, 0),
    [groups]
  );

  const pages = useMemo(() => paginateGroups(groups, ROWS_PER_PAGE), [groups]);
  const pageCount   = pages.length;
  const currentPage = Number.isFinite(pageParam)
    ? Math.min(Math.max(pageParam, 1), Math.max(pageCount, 1))
    : 1;
  const pageSegments = pages[currentPage - 1] || [];
  const firstRow = (currentPage - 1) * ROWS_PER_PAGE + 1;
  const lastRow  = Math.min(currentPage * ROWS_PER_PAGE, visibleRowCount);

  // Any filter change goes back to page 1 (page is dropped unless the
  // patch sets it explicitly).
  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    if (!("page" in patch)) next.delete("page");
    Object.entries(patch).forEach(([k, v]) => {
      if (
        v === null || v === undefined || v === "" ||
        v === "ALL" || v === PERIOD.ALL || v === STATUS.ALL || v === RESULT.ALL ||
        (k === "page" && Number(v) === 1)
      ) {
        next.delete(k);
      } else {
        next.set(k, v);
      }
    });
    setSearchParams(next);
  };

  const setLotteryFilter = (next) => updateParams({ lottery: next === "ALL" ? null : next });
  const setStatusFilter  = (next) => updateParams({ status: next });
  const setResultFilter  = (next) => updateParams({ result: next });
  const setPeriod = (next) =>
    next === PERIOD.CUSTOM
      ? updateParams({ period: PERIOD.CUSTOM })
      : updateParams({ period: next, from: null, to: null });
  const setCustomFrom = (v) => updateParams({ period: PERIOD.CUSTOM, from: v });
  const setCustomTo   = (v) => updateParams({ period: PERIOD.CUSTOM, to: v });
  const clearDateFilters = () => updateParams({ period: null, from: null, to: null });
  const clearAllFilters  = () =>
    updateParams({ period: null, from: null, to: null, status: null, result: null });
  const goToPage = (n) => {
    updateParams({ page: n });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasDateFilter   = period !== PERIOD.ALL;
  const hasStatusFilter = statusFilter !== STATUS.ALL;
  const hasResultFilter = resultFilter !== RESULT.ALL;

  return (
    <AppShell>
      <div className="px-4 sm:px-6 py-6 max-w-[880px] mx-auto">

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 text-[12px] mb-3 hover:underline"
          style={{ color: "var(--txt-2)" }}
        >
          <Icon name="arrow-left" size={13} />
          Back to dashboard
        </button>

        <header className="mb-5">
          <h1 className="font-semibold mb-1" style={{ fontSize: 26, color: "var(--txt)" }}>
            Picks history
          </h1>
          <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
            Every game you've generated, grouped by drawing, with the official result and your prizes.
          </p>
        </header>

        {!loading && !errored && games.length > 0 && (
          <SummaryTiles summary={summary} />
        )}

        <Card variant="soft" className="mb-4">
          <div className="mb-3">
            <div className="lg-label mb-2">Lottery</div>
            <div className="flex flex-wrap gap-2">
              <FilterChip label="All"           active={lotteryFilter === "ALL"}                 onClick={() => setLotteryFilter("ALL")} />
              <FilterChip label="Mega Millions" active={lotteryFilter === LOTTERY.MEGA_MILLIONS} onClick={() => setLotteryFilter(LOTTERY.MEGA_MILLIONS)} />
              <FilterChip label="Powerball"     active={lotteryFilter === LOTTERY.POWER_BALL}    onClick={() => setLotteryFilter(LOTTERY.POWER_BALL)} />
            </div>
          </div>

          <div className="mb-3">
            <div className="lg-label mb-2">Results</div>
            <div className="flex flex-wrap gap-2">
              <FilterChip label="All"       active={resultFilter === RESULT.ALL}     onClick={() => setResultFilter(RESULT.ALL)} />
              <FilterChip label="With hits" active={resultFilter === RESULT.HITS}    onClick={() => setResultFilter(RESULT.HITS)} />
              <FilterChip label="Winners"   active={resultFilter === RESULT.WINNERS} onClick={() => setResultFilter(RESULT.WINNERS)} />
            </div>
          </div>

          <div className="mb-3">
            <div className="lg-label mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              <FilterChip label="All"              active={statusFilter === STATUS.ALL}     onClick={() => setStatusFilter(STATUS.ALL)} />
              <FilterChip label="Drawing pending" active={statusFilter === STATUS.PENDING} onClick={() => setStatusFilter(STATUS.PENDING)} />
              <FilterChip label="Drawn"            active={statusFilter === STATUS.DRAWN}   onClick={() => setStatusFilter(STATUS.DRAWN)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="lg-label">Draw date</div>
              {hasDateFilter && (
                <button
                  type="button"
                  onClick={clearDateFilters}
                  className="text-[11px] hover:underline"
                  style={{ color: "var(--txt-2)" }}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <FilterChip label="All time"     active={period === PERIOD.ALL}    onClick={() => setPeriod(PERIOD.ALL)} />
              <FilterChip label="Past 7 days"  active={period === PERIOD.WEEK}   onClick={() => setPeriod(PERIOD.WEEK)} />
              <FilterChip label="Past 30 days" active={period === PERIOD.MONTH}  onClick={() => setPeriod(PERIOD.MONTH)} />
              <FilterChip label="This month"   active={period === PERIOD.M_TD}   onClick={() => setPeriod(PERIOD.M_TD)} />
              <FilterChip label="This year"    active={period === PERIOD.YEAR}   onClick={() => setPeriod(PERIOD.YEAR)} />
              <FilterChip label="Custom range" active={period === PERIOD.CUSTOM} onClick={() => setPeriod(PERIOD.CUSTOM)} />
            </div>

            {period === PERIOD.CUSTOM && (
              <div className="flex flex-wrap items-center gap-2 text-[12px]" style={{ color: "var(--txt-2)" }}>
                <label className="inline-flex items-center gap-1.5">
                  From
                  <input
                    type="date"
                    value={fromParam}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="px-2 py-1 rounded-md text-[12px]"
                    style={{
                      background: "var(--surf)",
                      color: "var(--txt)",
                      border: "1px solid var(--border-2)",
                    }}
                  />
                </label>
                <label className="inline-flex items-center gap-1.5">
                  To
                  <input
                    type="date"
                    value={toParam}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="px-2 py-1 rounded-md text-[12px]"
                    style={{
                      background: "var(--surf)",
                      color: "var(--txt)",
                      border: "1px solid var(--border-2)",
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </Card>

        {!loading && !errored && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-[12px]" style={{ color: "var(--txt-3)" }}>
            <span>
              {visibleRowCount} game{visibleRowCount === 1 ? "" : "s"} in {groups.length} drawing{groups.length === 1 ? "" : "s"}
              {hasDateFilter   ? ` · ${periodLabel}` : ""}
              {hasStatusFilter ? ` · ${statusFilter === STATUS.PENDING ? "Drawing pending" : "Drawn"}` : ""}
              {hasResultFilter ? ` · ${resultFilter === RESULT.HITS ? "With hits" : "Winners"}` : ""}
            </span>
            <Legend lotteryFilter={lotteryFilter} />
          </div>
        )}

        {loading ? (
          <Card>
            <div className="flex items-center justify-center gap-2 py-8">
              <Spinner size={18} />
              <span className="text-[13px]" style={{ color: "var(--txt-2)" }}>
                Loading your picks…
              </span>
            </div>
          </Card>
        ) : errored ? (
          <Card className="text-center py-10">
            <Icon name="alert-circle" size={32} style={{ color: "var(--red)" }} />
            <h2 className="font-medium mt-3 mb-1" style={{ color: "var(--txt)" }}>
              We couldn't load your picks.
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              The server didn't respond. Refresh the page or try again in a moment.
            </p>
          </Card>
        ) : groups.length === 0 ? (
          <Card className="text-center py-10">
            <Icon name="ticket-off" size={36} style={{ color: "var(--txt-3)" }} />
            <h2 className="font-medium mt-3 mb-1" style={{ color: "var(--txt)" }}>
              {games.length === 0
                ? "No picks here yet."
                : "No picks match these filters."}
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              {games.length === 0
                ? "Generate your first pick to start your history."
                : "Try widening the date range, switching the lottery, or clearing the filters."}
            </p>
            {games.length === 0 ? (
              <Button onClick={() => navigate("/generator")}>
                <Icon name="sparkles" size={14} />
                Generate now
              </Button>
            ) : (
              <Button variant="outline" onClick={clearAllFilters}>
                Clear filters
              </Button>
            )}
          </Card>
        ) : (
          <>
            {pageSegments.map((seg) => (
              <DrawingGroup
                key={`${seg.group.key}-${currentPage}`}
                group={seg.group}
                rows={seg.rows}
                continued={seg.continued}
                continues={seg.continues}
              />
            ))}
            {pageCount > 1 && (
              <Pagination
                page={currentPage}
                pageCount={pageCount}
                firstRow={firstRow}
                lastRow={lastRow}
                total={visibleRowCount}
                onChange={goToPage}
              />
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */

function SummaryTiles({ summary }) {
  const { totalGames, credits, totalWon, best } = summary;
  const bestLabel = best
    ? best.prize > 0
      ? formatPrize(best.prize)
      : `${best.hitCount} hit${best.hitCount === 1 ? "" : "s"}`
    : "—";
  const bestSub = best
    ? `${tierLabel(best)} · ${formatDrawDate(best.drawDate)}`
    : "No drawn games yet";

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      <SummaryTile label="Games"         value={totalGames.toLocaleString("en-US")} />
      <SummaryTile label="Credits spent" value={credits.toLocaleString("en-US")} />
      <SummaryTile label="Total won"     value={formatPrize(totalWon)} accent={totalWon > 0} />
      <SummaryTile label="Best result"   value={bestLabel} sub={bestSub} accent={best?.prize > 0} />
    </div>
  );
}

function SummaryTile({ label, value, sub, accent = false }) {
  return (
    <Card variant="soft">
      <div className="lg-label mb-1">{label}</div>
      <div
        className="font-semibold tabular-nums"
        style={{ fontSize: 20, color: accent ? "var(--teal)" : "var(--txt)" }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[11px] mt-0.5" style={{ color: "var(--txt-3)" }}>
          {sub}
        </div>
      )}
    </Card>
  );
}

function Legend({ lotteryFilter }) {
  const showPb = lotteryFilter !== LOTTERY.MEGA_MILLIONS;
  const showMb = lotteryFilter !== LOTTERY.POWER_BALL;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {showPb && (
        <span className="inline-flex items-center gap-1">
          <Ball value="" size="xs" variant="powerball" /> Powerball
        </span>
      )}
      {showMb && (
        <span className="inline-flex items-center gap-1">
          <Ball value="" size="xs" variant="megaball" /> Mega Ball
        </span>
      )}
      <span className="inline-flex items-center gap-1">
        <Ball value="" size="xs" hit /> Matched
      </span>
    </span>
  );
}

function DrawingGroup({ group, rows, continued = false, continues = false }) {
  const { type, draw, orders, games } = group;
  const drawn       = drawnMainNumbers(draw);
  const isDrawn     = drawn.length === 5;
  const specialName = specialBallLabel(type);

  // Numbers any game in this drawing matched — highlighted on the official result.
  const matchedMain = new Set();
  let extraMatchedAny = false;
  games.forEach((g) => {
    g._a.hitMain.forEach((n) => matchedMain.add(n));
    if (g._a.extraHit) extraMatchedAny = true;
  });

  const withHits = games.filter((g) => g._a.hasHit).length;
  const winners  = games.filter((g) => g._a.prize > 0).length;
  const won      = games.reduce((s, g) => s + g._a.prize, 0);
  const scored   = games.some((g) => g._a.scored);

  const totalCredits = orders.reduce((s, o) => s + (Number(o.creditUsed) || 0), 0);
  const singleOrder  = orders.length === 1 ? orders[0] : null;

  return (
    <Card className="mb-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="lg-label mb-1">{lotteryName(type)}</div>
          <div className="font-semibold text-[15px]" style={{ color: "var(--txt)" }}>
            {formatDrawDate(draw?.date)}
            {continued && (
              <span className="ml-2 text-[11px] font-normal" style={{ color: "var(--txt-3)" }}>
                (continued)
              </span>
            )}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--txt-3)" }}>
            {singleOrder ? `Order #${singleOrder.id} · ` : `${orders.length} orders · `}
            {games.length} game{games.length === 1 ? "" : "s"}
            {totalCredits > 0 ? ` · ${formatCredits(totalCredits)}` : ""}
          </div>
        </div>

        <div className="min-w-0">
          <div className="lg-label mb-1 sm:text-right">Official result</div>
          {isDrawn ? (
            <BallsRow>
              {drawn.map((n, i) => (
                <Ball
                  key={`o-${i}`}
                  value={n}
                  size="xs"
                  hit={matchedMain.has(n)}
                  ariaLabel={`${n}${matchedMain.has(n) ? ", matched by your picks" : ""}`}
                />
              ))}
              {draw?.extraBall !== null && draw?.extraBall !== undefined && (
                <Ball
                  value={draw.extraBall}
                  size="xs"
                  variant={specialBallVariant(type)}
                  hit={extraMatchedAny}
                  ariaLabel={`${specialName} ${draw.extraBall}${extraMatchedAny ? ", matched by your picks" : ""}`}
                />
              )}
            </BallsRow>
          ) : (
            <div className="text-[12px] font-medium" style={{ color: "var(--txt-2)" }}>
              Awaiting drawing
            </div>
          )}
        </div>
      </div>

      {isDrawn && (
        <div className="text-[12px] mt-2" style={{ color: "var(--txt-2)" }}>
          {scored ? (
            <>
              {withHits} game{withHits === 1 ? "" : "s"} with hits
              {" · "}
              {winners} winner{winners === 1 ? "" : "s"}
              {" · "}
              <span style={{ color: won > 0 ? "var(--teal)" : undefined, fontWeight: won > 0 ? 600 : 400 }}>
                {formatPrize(won)} won
              </span>
            </>
          ) : (
            "Awaiting results"
          )}
        </div>
      )}

      <div className="mt-3" style={{ borderTop: "1px solid var(--border)" }}>
        {orders.map((order) => {
          const orderRows = rows.filter((g) => g?.buyDraw?.id === order.id);
          if (orderRows.length === 0) return null;
          return (
            <div key={order.id}>
              {!singleOrder && (
                <div className="text-[11px] pt-2.5 pb-1" style={{ color: "var(--txt-3)" }}>
                  Order #{order.id} · {order.count} game{order.count === 1 ? "" : "s"}
                  {Number(order.creditUsed) > 0 ? ` · ${formatCredits(Number(order.creditUsed))}` : ""}
                </div>
              )}
              <div role="list">
                {orderRows.map((g, idx) => (
                  <GameRow
                    key={`${g?.id ?? "k"}-${idx}`}
                    game={g}
                    isLast={idx === orderRows.length - 1}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {continues && (
        <div className="text-[11px] pt-2 text-right" style={{ color: "var(--txt-3)" }}>
          Continues on the next page →
        </div>
      )}
    </Card>
  );
}

/**
 * Prev / numbered pages / Next. Long ranges collapse to
 * 1 … (page-1) page (page+1) … last.
 */
function Pagination({ page, pageCount, firstRow, lastRow, total, onChange }) {
  const items = pageItems(page, pageCount);
  const btn = (active) => ({
    background: active ? "var(--gold)" : "var(--surf-2)",
    color: active ? "#0E2A18" : "var(--txt-2)",
    border: `1px solid ${active ? "var(--gold)" : "var(--border-2)"}`,
  });

  return (
    <nav aria-label="Picks pages" className="flex flex-col items-center gap-2 mt-4">
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1.5 rounded-full text-[12px] font-medium disabled:opacity-40"
          style={btn(false)}
        >
          ← Prev
        </button>
        {items.map((it, i) =>
          it === "…" ? (
            <span key={`gap-${i}`} className="px-1 text-[12px]" style={{ color: "var(--txt-3)" }}>…</span>
          ) : (
            <button
              key={it}
              type="button"
              onClick={() => onChange(it)}
              aria-current={it === page ? "page" : undefined}
              aria-label={`Page ${it}`}
              className="min-w-[32px] px-2.5 py-1.5 rounded-full text-[12px] font-medium tabular-nums"
              style={btn(it === page)}
            >
              {it}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= pageCount}
          className="px-3 py-1.5 rounded-full text-[12px] font-medium disabled:opacity-40"
          style={btn(false)}
        >
          Next →
        </button>
      </div>
      <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>
        Games {firstRow}–{lastRow} of {total} · Page {page} of {pageCount}
      </div>
    </nav>
  );
}

function GameRow({ game, isLast }) {
  const type = game?._type;
  const a = game._a;
  const specialName = specialBallLabel(type);
  const extra = game?.extraBall;

  return (
    <div
      role="listitem"
      className="flex items-center gap-3 py-2 min-h-[46px]"
      style={{ borderBottom: isLast ? "none" : "1px solid var(--border)" }}
    >
      <BallsRow className="flex-1 min-w-0">
        {a.mainNums.map((n, i) => {
          const hit = a.hitMain.has(n);
          return (
            <Ball
              key={`m-${i}`}
              value={n}
              size="sm"
              hit={hit}
              ariaLabel={`${n}${hit ? ", hit" : ""}`}
            />
          );
        })}
        {extra !== null && extra !== undefined && (
          <Ball
            value={extra}
            size="sm"
            variant={specialBallVariant(type)}
            hit={a.extraHit}
            ariaLabel={`${specialName} ${extra}${a.extraHit ? ", hit" : ""}`}
            className="ml-1"
          />
        )}
      </BallsRow>
      <div className="shrink-0">
        <StatusBadge game={game} />
      </div>
    </div>
  );
}

/**
 * One status per pick:
 *   - not scored yet       → neutral "Pending" / "Awaiting results"
 *   - scored, prize > 0    → solid green with tier + prize
 *   - scored, no prize     → neutral "N hits" or "—"
 */
function StatusBadge({ game }) {
  const a = game._a;
  if (!a.scored) {
    const pending = drawnMainNumbers(game?.buyDraw?.draw).length !== 5;
    return <Badge variant="neutral">{pending ? "Pending" : "Awaiting results"}</Badge>;
  }
  if (a.prize > 0) {
    return (
      <Badge variant="success">
        {game?.winner ? "Jackpot" : tierLabel(a)} · {formatPrize(a.prize)}
      </Badge>
    );
  }
  if (a.hitCount > 0) {
    return (
      <Badge variant="neutral">
        {a.hitCount} hit{a.hitCount === 1 ? "" : "s"}
      </Badge>
    );
  }
  return (
    <Badge variant="neutral">
      <span aria-hidden="true">—</span>
      <span className="sr-only">No hits</span>
    </Badge>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors"
      style={{
        background: active ? "var(--gold)" : "var(--surf-2)",
        color: active ? "#0E2A18" : "var(--txt-2)",
        border: `1px solid ${active ? "var(--gold)" : "var(--border-2)"}`,
      }}
    >
      {label}
    </button>
  );
}

/* ============================================================
   Helpers
   ============================================================ */

function specialBallLabel(type) {
  return type === LOTTERY.MEGA_MILLIONS ? "Mega Ball" : "Powerball";
}

function specialBallShort(type) {
  return type === LOTTERY.MEGA_MILLIONS ? "MB" : "PB";
}

function specialBallVariant(type) {
  return type === LOTTERY.MEGA_MILLIONS ? "megaball" : "powerball";
}

function drawnMainNumbers(draw) {
  const nums = [draw?.num1, draw?.num2, draw?.num3, draw?.num4, draw?.num5];
  return nums.every((n) => n !== null && n !== undefined && Number.isFinite(Number(n)))
    ? nums.map(Number)
    : [];
}

/**
 * Which of the pick's main numbers hit. Prefers the official drawing
 * carried in buyDraw.draw; falls back to parsing matchedNumbersDetail
 * ("14, 52, Extra Ball: 9") when the draw numbers aren't in the payload.
 */
function resolveHitMain(mainNums, drawnMain, detail) {
  if (drawnMain.length === 5) {
    return new Set(mainNums.filter((n) => drawnMain.includes(n)));
  }
  const main = String(detail || "").split(/extra ball/i)[0];
  const parsed = (main.match(/\d+/g) || []).map(Number);
  return new Set(mainNums.filter((n) => parsed.includes(n)));
}

/** Precomputes the per-pick match data once, stored on `_a`. */
function analyzeGame(game) {
  const mainNums = [game?.num1, game?.num2, game?.num3, game?.num4, game?.num5];
  const scored   = game?.prizeValueUsd !== null && game?.prizeValueUsd !== undefined;
  const drawn    = drawnMainNumbers(game?.buyDraw?.draw);
  const hitMain  = scored ? resolveHitMain(mainNums, drawn, game?.matchedNumbersDetail) : new Set();
  const extraHit = scored && game?.extraMatched === true;
  return {
    ...game,
    _a: {
      mainNums,
      scored,
      hitMain,
      hitCount: hitMain.size,
      extraHit,
      hasHit: hitMain.size > 0 || extraHit,
      prize: scored ? Number(game.prizeValueUsd) || 0 : 0,
      type: game?._type,
      drawDate: game?.buyDraw?.draw?.date,
    },
  };
}

/** "3+PB", "4", "0+MB" — the prize tier in the lottery's own shorthand. */
function tierLabel(a) {
  return a.extraHit ? `${a.hitCount}+${specialBallShort(a.type)}` : `${a.hitCount}`;
}

function formatPrize(v) {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? `$${n.toLocaleString("en-US")}` : formatUsd(n);
}

function formatCredits(n) {
  return `${n.toLocaleString("en-US")} credit${n === 1 ? "" : "s"}`;
}

/**
 * Groups picks by drawing (newest first) and, inside each drawing, by
 * order (buy-draw). Credits come from the order, not the pick, since
 * `buyDraw.creditUsed` is the order total.
 */
function groupByDrawing(games) {
  const byKey = new Map();
  games.forEach((g) => {
    const draw = g?.buyDraw?.draw;
    const key = `${g?._type}-${draw?.id ?? draw?.date ?? `bd${g?.buyDraw?.id}`}`;
    let grp = byKey.get(key);
    if (!grp) {
      grp = { key, type: g?._type, draw, ts: drawTs(g), ordersById: new Map(), games: [] };
      byKey.set(key, grp);
    }
    grp.games.push(g);
    const orderId = g?.buyDraw?.id;
    const order = grp.ordersById.get(orderId);
    if (order) {
      order.count += 1;
    } else {
      grp.ordersById.set(orderId, { id: orderId, creditUsed: g?.buyDraw?.creditUsed, count: 1 });
    }
  });

  return [...byKey.values()]
    .map((grp) => ({
      ...grp,
      orders: [...grp.ordersById.values()].sort((a, b) => (b.id ?? 0) - (a.id ?? 0)),
      games: grp.games.sort((a, b) => (a.id ?? 0) - (b.id ?? 0)),
    }))
    .sort((a, b) => b.ts - a.ts);
}

/**
 * Splits the grouped rows into pages of `pageSize` rows. A drawing that
 * doesn't fit is cut and continues on the next page, where its header is
 * rendered again (`continued`); `continues` marks the cut on the page
 * where it starts.
 */
function paginateGroups(groups, pageSize) {
  const pages = [];
  let current = [];
  let used = 0;

  groups.forEach((group) => {
    let start = 0;
    while (start < group.rows.length) {
      if (used === pageSize) {
        pages.push(current);
        current = [];
        used = 0;
      }
      const take = Math.min(pageSize - used, group.rows.length - start);
      const end = start + take;
      current.push({
        group,
        rows: group.rows.slice(start, end),
        continued: start > 0,
        continues: end < group.rows.length,
      });
      used += take;
      start = end;
    }
  });

  if (current.length > 0) pages.push(current);
  return pages;
}

function pageItems(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const set = new Set([1, pageCount, page - 1, page, page + 1]);
  const nums = [...set].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
}

function summarize(games) {
  const seenOrders = new Set();
  let credits = 0;
  let totalWon = 0;
  let best = null;

  games.forEach((g) => {
    const orderId = g?.buyDraw?.id;
    if (orderId !== undefined && !seenOrders.has(orderId)) {
      seenOrders.add(orderId);
      credits += Number(g?.buyDraw?.creditUsed) || 0;
    }
    const a = g._a;
    totalWon += a.prize;
    if (!a.scored) return;
    const better =
      !best ||
      a.prize > best.prize ||
      (a.prize === best.prize && a.hitCount + (a.extraHit ? 0.5 : 0) > best.hitCount + (best.extraHit ? 0.5 : 0));
    if (better) best = a;
  });

  return { totalGames: games.length, credits, totalWon, best };
}

function drawTs(game) {
  const raw = game?.buyDraw?.draw?.date || game?.buyDraw?.date || null;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function resolvePeriod(period, fromParam, toParam) {
  const now = Date.now();
  const day = 86400000;

  switch (period) {
    case PERIOD.WEEK:
      return { fromTs: now - 7  * day, toTs: null, periodLabel: "Past 7 days" };
    case PERIOD.MONTH:
      return { fromTs: now - 30 * day, toTs: null, periodLabel: "Past 30 days" };
    case PERIOD.M_TD: {
      const d = new Date();
      d.setDate(1); d.setHours(0, 0, 0, 0);
      return { fromTs: d.getTime(), toTs: null, periodLabel: "This month" };
    }
    case PERIOD.YEAR: {
      const d = new Date();
      d.setMonth(0, 1); d.setHours(0, 0, 0, 0);
      return { fromTs: d.getTime(), toTs: null, periodLabel: "This year" };
    }
    case PERIOD.CUSTOM: {
      const fromTs = fromParam ? new Date(`${fromParam}T00:00:00`).getTime() : null;
      const toTs   = toParam   ? new Date(`${toParam}T23:59:59.999`).getTime() : null;
      const label =
        fromParam && toParam ? `${fromParam} → ${toParam}` :
        fromParam            ? `From ${fromParam}` :
        toParam              ? `Until ${toParam}` :
                               "Custom range";
      return { fromTs: Number.isFinite(fromTs) ? fromTs : null,
               toTs:   Number.isFinite(toTs)   ? toTs   : null,
               periodLabel: label };
    }
    default:
      return { fromTs: null, toTs: null, periodLabel: "All time" };
  }
}
